import { useState } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { isEqual } from 'lodash';
import { POLLING_CONSTANTS } from '@shared/lib/contants';

export interface PollingConfig<TData> {
  // Query key for react-query
  queryKey: string | string[];
  // Function to get data
  fetchData: () => Promise<TData>;
  // Max retries before timing out
  maxRetries?: number;
  // Delay between retries in milliseconds
  retryDelayMs?: number;
  // Status field name in data
  statusField?: keyof TData;
  // Complete statuses that should stop polling
  completeStatuses?: string[];
  // Callbacks for various events
  onDataReceived?: (data: TData) => void;
  onComplete?: (data: TData, status: string) => void;
  onTimeout?: (data: TData) => void;
  // Additional query options
  queryOptions?: Omit<UseQueryOptions<TData, unknown, TData>, 'queryKey' | 'queryFn' | 'refetchInterval'>;
}

export function usePolling<TData>({
  queryKey,
  fetchData,
  maxRetries = POLLING_CONSTANTS.MAX_RETRIES,
  retryDelayMs = POLLING_CONSTANTS.RETRY_DELAY_MS,
  statusField = 'status' as keyof TData,
  completeStatuses = POLLING_CONSTANTS.COMPLETE_STATUSES,
  onDataReceived,
  onComplete,
  onTimeout,
  queryOptions = {},
}: PollingConfig<TData>) {
  const [retries, setRetries] = useState(0);
  const [previousData, setPreviousData] = useState<TData | null>(null);

  const { data, error, isLoading, isError, refetch } = useQuery<TData>(
    Array.isArray(queryKey) ? queryKey : [queryKey],
    async () => {
      const data = await fetchData();

      const isDataUpdated = !isEqual(previousData, data);

      if (isDataUpdated) {
        setRetries(0);
      } else {
        setRetries(prev => prev + 1);
      }

      setPreviousData(data);

      if (onDataReceived) {
        onDataReceived(data);
      }

      return data;
    },
    {
      ...queryOptions,
      refetchInterval: data => {
        if (!data) {
          return retryDelayMs;
        }

        const status = String(data[statusField]).toLowerCase();

        if (completeStatuses.includes(status)) {
          if (onComplete) {
            onComplete(data, status);
          }

          return false;
        }

        if (retries > maxRetries) {
          if (onTimeout) {
            onTimeout(data);
          }

          return false;
        }

        return retryDelayMs;
      },
    }
  );

  return {
    data,
    error,
    isLoading,
    isError,
    refetch,
  };
}
